import { useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";

// The combined DE/EN text an administrator stored before the templates were split by language
// (@external-adr GENESIS-0097-a-mail-is-written-in-the-language-resolved-for-its-recipient). It is kept unchanged and
// sent for every language without its own text, never translated. Discarding is final, so it asks first; a failed
// discard keeps the text and says so.
export function LegacyEmailTemplateNotice({
  subject,
  body,
  writable,
  failure,
  onDiscard,
}: {
  subject: string | null;
  body: string | null;
  writable: boolean;
  failure: string | null;
  onDiscard: () => Promise<void>;
}) {
  const intl = useIntl();
  const [confirming, setConfirming] = useState(false);
  const discardLabel = intl.formatMessage({
    id: "adminSettings.emailTemplates.legacy.discard",
    defaultMessage: "Discard bilingual text",
  });

  return (
    <div className="smycles-hint space-y-3 text-smycles-meta text-ink" role="note">
      <p>
        {intl.formatMessage({
          id: "adminSettings.emailTemplates.legacy.notice",
          defaultMessage:
            "This template still has a bilingual text from before the split by language. It is sent unchanged and untranslated for every language without its own text.",
        })}
      </p>
      <div>
        <div className="smycles-field-label">
          {intl.formatMessage({ id: "adminSettings.emailTemplates.legacy.title", defaultMessage: "Bilingual text" })}
        </div>
        {subject ? <div className="mt-1 font-medium text-ink">{subject}</div> : null}
        {body ? <p className="mt-1 whitespace-pre-line text-ink">{body}</p> : null}
      </div>
      {writable ? (
        <Button variant="ghost" size="sm" onClick={() => setConfirming(true)}>
          {discardLabel}
        </Button>
      ) : null}
      {failure ? <p role="alert">{failure}</p> : null}
      <ConfirmDialog
        open={confirming}
        title={discardLabel}
        message={intl.formatMessage({
          id: "adminSettings.emailTemplates.legacy.discardHint",
          defaultMessage:
            "Afterwards every language without its own text gets the built-in text. The bilingual text cannot be restored.",
        })}
        confirmLabel={discardLabel}
        onCancel={() => setConfirming(false)}
        onConfirm={async () => {
          setConfirming(false);
          await onDiscard();
        }}
      />
    </div>
  );
}
